// Lado CLIENTE de la regla del mixto (lib/menu/includedSelections.ts): lo que necesitan
// los modales de selección (POS, kiosko, menú web) para pintar cuánto cuesta la
// siguiente opción y para armar la línea del carrito con precio y rol por fila.
//
// 🔴 Una sola cuenta para pantalla, carrito y servidor. La línea guarda en `price_delta`
// lo que se cobra por unidad (con el extra ya sumado), así que calculateOrderTotals,
// cartItemLineTotal y el recibo suman bien sin saber nada de la regla. El servidor la
// recalcula igual desde la BD (lib/menu/includedSelectionsServer.ts) y no confía en
// estas filas: esto es para que lo que el cliente VE sea lo que se le cobra.
//
// Spec: docs/superpowers/specs/2026-09-18-selecciones-incluidas-mixto-design.md §8

import {
  applyIncludedSelections,
  includedSelectionsRuleOf,
  type IncludedSelectionsRule,
  type SelectionRole,
} from "@/lib/menu/includedSelections"

export type RuleOptionLike = {
  id: string
  price_delta?: number | null
  uses_all_included?: boolean | null
}

/** Grupo del catálogo de cualquier superficie: el POS y el menú web traen `options`,
 *  el kiosko `modifier_options`. */
export type RuleGroupLike = {
  id: string
  included_selections?: number | null
  extra_selection_price?: number | string | null
  included_shared_portion?: boolean | null
  zonable?: boolean | null
  options?: ReadonlyArray<RuleOptionLike>
  modifier_options?: ReadonlyArray<RuleOptionLike>
}

const optionsOf = (g: RuleGroupLike): ReadonlyArray<RuleOptionLike> => g.options ?? g.modifier_options ?? []

/** Regla del grupo, o `null` (sin cupo, zoneado, o columna ausente). */
export function groupRule(g: RuleGroupLike): IncludedSelectionsRule | null {
  return includedSelectionsRuleOf(g)
}

export type RuledSelectionRow = {
  optionId: string
  quantity: number
  role: SelectionRole
  /** `price_delta` crudo (TARJETA) por unidad, con el extra si es extra. */
  priceDelta: number
}

/**
 * Filas del motor para UN grupo con regla, sobre su selección EN ORDEN (el orden en que
 * se tocaron las opciones decide cuáles forman el mixto). `null` = el grupo no tiene
 * regla. Una opción que el catálogo del grupo no trae no entra.
 */
export function ruledGroupRows(
  g: RuleGroupLike,
  selected: ReadonlyArray<{ optionId: string; quantity: number }>,
): RuledSelectionRow[] | null {
  const rule = groupRule(g)
  if (!rule) return null
  const byId = new Map(optionsOf(g).map((o) => [o.id, o]))
  const picked = selected.filter((s) => byId.has(s.optionId) && s.quantity > 0)
  if (picked.length === 0) return []
  const rows = applyIncludedSelections(
    picked.map((s) => ({
      groupId: g.id,
      quantity: s.quantity,
      priceDelta: Number(byId.get(s.optionId)!.price_delta ?? 0) || 0,
      usesAllIncluded: byId.get(s.optionId)!.uses_all_included === true,
    })),
    new Map([[g.id, rule]]),
  ) ?? []
  return rows.map((r) => ({
    optionId: picked[r.source].optionId,
    quantity: r.quantity,
    role: r.role as SelectionRole,
    priceDelta: r.priceDelta,
  }))
}

const rawTotal = (rows: ReadonlyArray<RuledSelectionRow>) =>
  Math.round(rows.reduce((s, r) => s + r.priceDelta * r.quantity, 0) * 100) / 100

/**
 * Cuánto suma (crudo, TARJETA) agregar UNA unidad de `optionId` a la selección actual
 * del grupo. Es lo que la pantalla pinta al lado de cada opción: «Incluida» si es 0,
 * «+$X» si no. Con una opción «va sola» puede ser más que el extra, porque las que ya
 * estaban pasan a cobrarse.
 */
export function ruledAddCost(
  g: RuleGroupLike,
  selected: ReadonlyArray<{ optionId: string; quantity: number }>,
  optionId: string,
): number {
  const before = ruledGroupRows(g, selected)
  if (!before) return Number(optionsOf(g).find((o) => o.id === optionId)?.price_delta ?? 0) || 0
  const after = ruledGroupRows(g, [...selected, { optionId, quantity: 1 }]) ?? []
  return Math.round((rawTotal(after) - rawTotal(before)) * 100) / 100
}

/** Rol de cada opción elegida. Si una opción quedó partida (incluida + extra), manda
 *  `extra`: a esa opción se le cobra algo. */
export function ruledRoleByOption(rows: ReadonlyArray<RuledSelectionRow>): Map<string, SelectionRole> {
  const out = new Map<string, SelectionRole>()
  for (const r of rows) {
    if (out.get(r.optionId) !== "extra") out.set(r.optionId, r.role)
  }
  return out
}

type LineModifierLike = {
  modifier_group_id: string
  modifier_option_id: string
  price_delta: number
  quantity?: number
  selection_role?: SelectionRole
}

function withoutRole<M extends LineModifierLike>(m: M): M {
  if (!("selection_role" in m)) return m
  const copy = { ...m }
  delete copy.selection_role
  return copy
}

/**
 * Aplica las reglas de los grupos del plato a los modificadores de UNA línea del
 * carrito, en el orden en que vienen (el de la selección).
 *
 * - Filas de un grupo con regla: `price_delta = toStoredPrice(opción + extra)` y
 *   `selection_role`; una fila que cruza el cupo se parte en dos.
 * - El precio base sale del CATÁLOGO (`groups`), no de la fila: re-aplicarla sobre una
 *   línea ya cotizada (editar desde el carrito) no suma el extra dos veces.
 * - Sin grupos con regla y sin roles viejos: devuelve el MISMO arreglo.
 *
 * `toStoredPrice` convierte el precio crudo al que guarda la superficie: el POS y el
 * kiosko guardan el crudo (identidad) y el menú web el efectivo (`effectiveUnitPrice`).
 */
export function applyRulesToLineModifiers<M extends LineModifierLike>(
  modifiers: M[],
  groups: ReadonlyArray<RuleGroupLike>,
  toStoredPrice: (raw: number) => number = (raw) => raw,
): M[] {
  const rules = new Map<string, IncludedSelectionsRule>()
  const facts = new Map<string, { groupId: string; priceDelta: number; usesAllIncluded: boolean }>()
  for (const g of groups) {
    const rule = groupRule(g)
    if (!rule) continue
    rules.set(g.id, rule)
    for (const o of optionsOf(g)) {
      facts.set(o.id, { groupId: g.id, priceDelta: Number(o.price_delta ?? 0) || 0, usesAllIncluded: o.uses_all_included === true })
    }
  }

  const rows =
    rules.size === 0
      ? null
      : applyIncludedSelections(
          modifiers.map((m) => {
            const f = facts.get(m.modifier_option_id)
            return {
              groupId: f?.groupId ?? null,
              quantity: m.quantity ?? 1,
              priceDelta: f?.priceDelta ?? 0,
              usesAllIncluded: f?.usesAllIncluded ?? false,
            }
          }),
          rules,
        )
  if (!rows) return modifiers.some((m) => "selection_role" in m) ? modifiers.map(withoutRole) : modifiers

  return rows.map((r) => {
    const m = modifiers[r.source]
    if (r.role === null) return withoutRole(m)
    return { ...m, quantity: r.quantity, price_delta: toStoredPrice(r.priceDelta), selection_role: r.role }
  })
}

export type RuleNote =
  /** Sin elegir: lo que suma tocarla ahora (efectivo). 0 = «Incluida». */
  | { kind: "add"; amount: number }
  /** Elegida: su rol y lo que se le cobra (efectivo). */
  | { kind: "role"; role: SelectionRole; amount: number }

/**
 * Lo que una pantalla pinta al lado de una opción de un grupo con regla, en lugar de su
 * precio. Una sola fuente para los tres modales (POS, kiosko, menú web): cada uno solo
 * decide el texto. `eff` convierte el precio crudo al que ve el cliente (config de
 * precios del local), por FILA, igual que cartItemLineTotal. `null` = grupo sin regla.
 */
export function ruleNoteOf(
  g: RuleGroupLike,
  selected: ReadonlyArray<{ optionId: string; quantity: number }>,
  optionId: string,
  isSelected: boolean,
  eff: (raw: number) => number = (raw) => raw,
): RuleNote | null {
  if (!groupRule(g)) return null
  if (!isSelected) {
    // La diferencia de totales ya efectivos: con precio único en efectivo, efectivo(a+b)
    // no siempre es efectivo(a)+efectivo(b), y el número tiene que ser el del botón.
    const before = ruledGroupRows(g, selected) ?? []
    const after = ruledGroupRows(g, [...selected, { optionId, quantity: 1 }]) ?? []
    const total = (rows: RuledSelectionRow[]) => rows.reduce((s, r) => s + eff(r.priceDelta) * r.quantity, 0)
    return { kind: "add", amount: Math.round((total(after) - total(before)) * 100) / 100 }
  }
  const rows = ruledGroupRows(g, selected) ?? []
  const mine = rows.filter((r) => r.optionId === optionId)
  return {
    kind: "role",
    role: ruledRoleByOption(mine).get(optionId) ?? "included",
    amount: Math.round(mine.reduce((s, r) => s + eff(r.priceDelta) * r.quantity, 0) * 100) / 100,
  }
}
